import { useRef, useState, type FormEvent } from 'react';
import { useOne } from '@refinedev/core';
import type { SessionBalance } from 'shared';
import { useSession } from '../../../lib/session.js';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { Field, FormErrors, TextInput } from '../../../components/Form.js';
import { api, ApiError } from '../../../lib/api.js';

interface TenantRecord {
  achAccess: {
    revokedAt: string | null;
    revokedReason: string | null;
    enrolled: boolean;
    bankName: string | null;
    bankLast4: string | null;
  };
}

/**
 * The tenant's payment interface — step 3's spike folded in as real Financial
 * Connections enrollment plus a manual "submit a payment" form.
 *
 * Manual by design: the tenant logs in and presses Submit themselves. There
 * is no autopay here and nothing on this page schedules a future debit.
 *
 * Three branches on `achAccess`, checked in this order:
 *
 *   1. `revokedAt` set — blocked, same as before this step. A returned
 *      payment revokes ACH across every lease this tenant is named on, not
 *      just the one the return landed on, so this reads off the tenant
 *      record rather than a per-lease flag.
 *   2. Not `enrolled` — link a bank account through Stripe's
 *      Financial-Connections modal.
 *   3. Enrolled — submit a payment against one of their leases.
 */
export function PayRent() {
  const { state } = useSession();
  const tenantId = state.status === 'signed-in' ? state.me.actor.tenantId : null;

  const { data, isLoading, refetch } = useOne<TenantRecord>({
    resource: 'tenants',
    id: tenantId ?? '',
    queryOptions: { enabled: Boolean(tenantId) },
  });

  if (state.status !== 'signed-in') return null;
  const { me } = state;

  const achAccess = data?.data.achAccess;

  return (
    <section>
      <h1>Pay rent</h1>

      <Bento>
        {isLoading || !achAccess ? (
          <BentoCell span="full">
            <p className="bento__hint">Loading…</p>
          </BentoCell>
        ) : achAccess.revokedAt ? (
          <BentoCell span="full" tone="attention" title="ACH is not available on your account">
            <p className="bento__hint">
              Your ability to pay by bank transfer (ACH) has been revoked because a recent payment
              was returned{achAccess.revokedReason ? ` — ${achAccess.revokedReason}` : ''}.
            </p>
            <p className="bento__sub">
              Please pay by <strong>Cashier's Check</strong> or <strong>Wire Transfer</strong>{' '}
              instead. Contact the management office for wire instructions or to arrange dropping
              off a check.
            </p>
          </BentoCell>
        ) : achAccess.enrolled ? (
          <PaymentForm
            balances={me.balances}
            bankName={achAccess.bankName}
            bankLast4={achAccess.bankLast4}
          />
        ) : (
          <EnrollmentCard onEnrolled={() => void refetch()} />
        )}
      </Bento>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Enrollment
// ---------------------------------------------------------------------------

interface StartEnrollmentResponse {
  clientSecret: string;
  publishableKey: string;
}

type EnrollStage = 'idle' | 'collecting' | 'mandate' | 'confirming';

function EnrollmentCard({ onEnrolled }: { onEnrolled: () => void }) {
  const [stage, setStage] = useState<EnrollStage>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const stripeRef = useRef<StripeClient | null>(null);
  const setupIntentRef = useRef<{ id: string; clientSecret: string } | null>(null);

  async function handleLink(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setStage('collecting');

    try {
      const { clientSecret, publishableKey } = await api<StartEnrollmentResponse>(
        '/ledger/enroll',
        {
          method: 'POST',
        },
      );

      if (!window.Stripe) {
        throw new Error('Stripe failed to load. Refresh the page and try again.');
      }
      const stripe = window.Stripe(publishableKey);
      stripeRef.current = stripe;

      // billing_details.name is REQUIRED by Stripe to create an ACH
      // PaymentMethod — omitting it fails at collection time with a message
      // that does not obviously say so.
      const { setupIntent, error: collectError } = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: { billing_details: { name, email } },
        },
        expand: ['payment_method'],
      });

      if (collectError) {
        setError(collectError.message);
        setStage('idle');
        return;
      }
      if (setupIntent?.status !== 'requires_confirmation') {
        setError('Bank account linking was not completed.');
        setStage('idle');
        return;
      }

      // The account is linked but NOT yet usable — confirmation is what
      // records mandate acceptance, and without it there is no
      // authorization to debit.
      setupIntentRef.current = { id: setupIntent.id, clientSecret };
      setStage('mandate');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong linking your bank account.',
      );
      setStage('idle');
    }
  }

  async function handleConfirm() {
    const stripe = stripeRef.current;
    const pending = setupIntentRef.current;
    if (!stripe || !pending) return;

    setError(null);
    setStage('confirming');

    try {
      const { setupIntent, error: confirmError } = await stripe.confirmUsBankAccountSetup(
        pending.clientSecret,
      );

      if (confirmError) {
        setError(confirmError.message);
        setStage('mandate');
        return;
      }
      if (setupIntent?.status !== 'succeeded') {
        setError('Bank account linking did not complete. Try again.');
        setStage('mandate');
        return;
      }

      // Re-confirms against Stripe server-side rather than trusting the
      // browser's copy — see stripe-service.ts's confirmEnrollment.
      await api('/ledger/enroll/confirm', {
        method: 'POST',
        body: JSON.stringify({ setupIntentId: setupIntent.id }),
      });

      onEnrolled();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong confirming your bank account.',
      );
      setStage('mandate');
    }
  }

  return (
    <BentoCell span="full" title="Link a bank account">
      <p className="bento__hint">
        Online payment by bank transfer (ACH). Linking is done through Stripe's Financial
        Connections — you log into your bank in a secure window, and we never see your credentials
        or account number.
      </p>

      {stage !== 'mandate' && stage !== 'confirming' ? (
        <form className="stack" onSubmit={(event) => void handleLink(event)}>
          <Field label="Name on the account" name="name" required>
            <TextInput name="name" value={name} onChange={setName} required />
          </Field>
          <Field label="Email" name="email" required>
            <TextInput name="email" type="email" value={email} onChange={setEmail} required />
          </Field>
          {error ? <p className="alert alert--error">{error}</p> : null}
          <button type="submit" className="button" disabled={stage === 'collecting'}>
            {stage === 'collecting' ? 'Opening…' : 'Link bank account'}
          </button>
        </form>
      ) : (
        <div className="stack">
          <div className="card">
            <strong>Authorization</strong>
            <p className="bento__hint">
              By clicking "Agree and continue", you authorize b1 property management to debit the
              bank account you just linked for any amount owed for charges arising from your lease,
              pursuant to your lease terms, until this authorization is revoked. You may amend or
              cancel this authorization at any time by providing 30 days' notice to the management
              office.
            </p>
          </div>
          {error ? <p className="alert alert--error">{error}</p> : null}
          <button
            type="button"
            className="button"
            onClick={() => void handleConfirm()}
            disabled={stage === 'confirming'}
          >
            {stage === 'confirming' ? 'Confirming…' : 'Agree and continue'}
          </button>
        </div>
      )}
    </BentoCell>
  );
}

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

/**
 * totalBalance/pastDueBalance don't yet reflect a payment still in flight —
 * see sessionBalanceSchema's own comment on pendingPaymentAmount. Defaulting
 * the amount field to totalBalance alone would have a tenant who already
 * submitted a partial payment walk straight into paying the pre-partial-
 * payment amount again. Floored at 0 for the (should-be-rare) case where a
 * pending payment already covers the whole balance.
 */
function defaultPayableAmount(balance: SessionBalance): string {
  const remaining = Number(balance.totalBalance) - Number(balance.pendingPaymentAmount ?? '0');
  return Math.max(remaining, 0).toFixed(2);
}

function PaymentForm({
  balances,
  bankName,
  bankLast4,
}: {
  balances: SessionBalance[];
  bankName: string | null;
  bankLast4: string | null;
}) {
  const [leaseId, setLeaseId] = useState(balances[0]?.leaseId ?? '');
  const [amount, setAmount] = useState(balances[0] ? defaultPayableAmount(balances[0]) : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [submitted, setSubmitted] = useState(false);

  const selected = balances.find((b) => b.leaseId === leaseId);
  const account = bankName
    ? `${bankName}${bankLast4 ? ` ••${bankLast4}` : ''}`
    : 'your linked bank account';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api('/ledger/pay', { method: 'POST', body: JSON.stringify({ leaseId, amount }) });
      setSubmitted(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <BentoCell span="full" title="Payment submitted">
        <p className="bento__hint">
          Your payment has been submitted from {account}. Bank transfers take a few business days to
          settle — it is credited to your balance once it does, not when you submit it.
        </p>
      </BentoCell>
    );
  }

  return (
    <BentoCell span="full" title="Pay rent">
      <p className="bento__sub">Paying from {account}.</p>

      {balances.length === 0 ? (
        <p className="bento__hint">Nothing is currently owed — there is no balance to pay.</p>
      ) : (
        <form className="stack" onSubmit={(event) => void handleSubmit(event)}>
          {balances.length > 1 ? (
            <Field label="Lease" name="leaseId">
              <select
                id="leaseId"
                name="leaseId"
                className="field__input"
                value={leaseId}
                onChange={(event) => {
                  const next = event.target.value;
                  setLeaseId(next);
                  const nextBalance = balances.find((b) => b.leaseId === next);
                  setAmount(nextBalance ? defaultPayableAmount(nextBalance) : '');
                }}
              >
                {balances.map((b) => (
                  <option key={b.leaseId} value={b.leaseId}>
                    {b.propertyName} — {b.unitLabel} (${b.totalBalance} owed)
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <p className="bento__hint">
              {selected?.propertyName} — {selected?.unitLabel}: ${selected?.totalBalance} owed
            </p>
          )}

          {selected && Number(selected.pendingPaymentAmount ?? '0') > 0 ? (
            <p className="bento__hint">
              A <strong>${selected.pendingPaymentAmount}</strong> payment on this lease is already
              processing and isn't reflected in the balance above yet — check before submitting
              another one.
            </p>
          ) : null}

          <Field label="Amount" name="amount" required>
            <TextInput name="amount" value={amount} onChange={setAmount} required />
          </Field>

          <FormErrors error={error} />
          <button type="submit" className="button" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit payment'}
          </button>
        </form>
      )}

      <p className="bento__hint">
        Bank transfers take some time to clear. A payment is credited to your balance when it
        settles, not when you submit it.
      </p>
    </BentoCell>
  );
}
